import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { AppContext } from '../../App'
import store from '../../store'
import { selectActiveChartId } from '../../selectors/chart'
import { selectActiveLayout, selectActiveLayoutId } from '../../selectors/home'
import useSetPrimarySecurity from '../../hooks/useSetPrimarySecurity'
import { useAppContextChart } from '../chartV2/useAppContextChart'
import { registerSurface } from './surfaceRegistry'
import { buildChartSurfaceMetadata, applyChartSurfaceArtifacts } from './domains/chartDomain'
import { resolveContextChartId } from '../chartV2/aiChat/chartContext'

export function useChartSurfaceRegistration(isChartRoute: boolean, enabled = true) {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const appContext = useContext(AppContext)
    const setPrimarySecurity = useSetPrimarySecurity()
    const activeChartId = useSelector(selectActiveChartId)
    const layout = useSelector(selectActiveLayout)
    const layoutId = useSelector(selectActiveLayoutId)
    const contextChartId = useMemo(() => {
        if (!isChartRoute) return ''
        return resolveContextChartId(store.getState(), activeChartId)
    }, [activeChartId, isChartRoute, layout?.activeWidget, layoutId])

    // Charts are attached imperatively to the AppContext ref, so reading `getChart`
    // during render never re-evaluates when one mounts. This hook tracks the instance
    // as state, which is what lets the surface flip to ready after navigation.
    const chart = useAppContextChart(isChartRoute ? contextChartId : '')
    const ready = Boolean(isChartRoute && contextChartId && chart)

    const buildMetadata = useCallback(() => {
        const state = store.getState()
        const chartId = resolveContextChartId(state, activeChartId)
        return buildChartSurfaceMetadata(state, chartId, appContext.getChart.bind(appContext))
    }, [activeChartId, appContext, layout?.activeWidget, layoutId])

    const applyArtifacts = useCallback(
        async (metadata: Record<string, unknown>) => {
            const state = store.getState()
            const chartId = resolveContextChartId(state, activeChartId)
            if (!chartId) {
                return { applied: [], failed: [{ op: 'apply', message: 'Chart not ready' }] }
            }
            const result = await applyChartSurfaceArtifacts(metadata, {
                viewKey: chartId,
                getChart: appContext.getChart.bind(appContext),
                dispatch,
                setPrimarySecurity,
                dashboard: appContext.dashboard,
                navigate,
                getChartSnapshot: () => {
                    const method = appContext.getChartSnapshot?.[chartId]
                    return method ? method() : null
                },
            })
            return result ?? { applied: [], failed: [] }
        },
        [activeChartId, appContext, dispatch, layout?.activeWidget, layoutId, navigate, setPrimarySecurity],
    )

    useEffect(() => {
        if (!enabled || !isChartRoute) return
        return registerSurface({
            domain: 'chart',
            ready,
            buildMetadata,
            applyArtifacts,
        })
    }, [applyArtifacts, buildMetadata, enabled, isChartRoute, ready])
}
